import { useEffect, useMemo, useState } from 'react'
import { Activity as ActivityIcon, Grid2X2, RefreshCw, Rocket, ShoppingBag, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getActivity, getCollections, getIndexedAssets, getLiveListings } from '../api'
import type { Activity, Collection, MarketAsset } from '../types'
import { CollectionCards } from '../components/CollectionCards'
import { CollectionTable } from '../components/CollectionTable'
import { CollectionViewToggle, type CollectionViewMode } from '../components/CollectionViewToggle'
import { ErrorState, Loading } from '../components/States'
import { MarketIndexCard, normalizeMarketAsset } from '../components/MarketIndexCard'
import { MarketLaunchCard } from '../components/MarketLaunchCard'
import { api } from '../api'
import { date, number, short, sol } from '../utils'

export default function MarketplacePage() {
  const [collections, setCollections] = useState<Collection[]>([])
  const [assets, setAssets] = useState<MarketAsset[]>([])
  const [activity, setActivity] = useState<Activity[]>([])
  const [launches, setLaunches] = useState<any[]>([])
  const [providerListings, setProviderListings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [collectionView, setCollectionView] = useState<CollectionViewMode>('cards')

  async function load() {
    setLoading(true)
    setError('')
    const results = await Promise.allSettled([
      getCollections(),
      getIndexedAssets(120),
      getActivity(24),
      getLiveListings(48),
      api<any[]>('/api/v1/launchpad?limit=100'),
    ])

    if (results[0].status === 'fulfilled') setCollections(results[0].value || [])
    if (results[1].status === 'fulfilled') {
      const rows = Array.isArray(results[1].value) ? results[1].value : []
      setAssets(rows.filter((row:any) => row && row.burnt !== true).map(normalizeMarketAsset))
    }
    if (results[2].status === 'fulfilled') setActivity(results[2].value || [])
    if (results[3].status === 'fulfilled') setProviderListings(results[3].value?.listings || [])
    if (results[4].status === 'fulfilled') setLaunches(Array.isArray(results[4].value) ? results[4].value : [])

    const failed = results.filter(result => result.status === 'rejected') as PromiseRejectedResult[]
    if (failed.length === results.length) setError(failed[0]?.reason?.message || 'Marketplace data unavailable')
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const providerMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const row of providerListings) {
      const id = String(row?.tokenMint || '')
      if (id) map.set(id, row)
    }
    return map
  }, [providerListings])

  const listed = useMemo(() => {
    const indexed = new Map(
      assets
        .filter((row:any) => row?.listed === true && Number((row as any)?.price_lamports || 0) > 0)
        .map(row => [String(row.asset_id), row])
    )
    const rows: Array<{asset: MarketAsset; verified: boolean}> = []

    for (const [id, provider] of providerMap.entries()) {
      const old = indexed.get(id)
      rows.push({
        verified: true,
        asset: normalizeMarketAsset({
          ...(old || {}),
          asset_id: id,
          name: provider?.name || old?.name || id,
          image_url: provider?.image || old?.image_url,
          listed: true,
          price_lamports: provider?.priceLamports || old?.price_lamports || 0,
        }),
      })
      indexed.delete(id)
    }
    for (const row of indexed.values()) rows.push({ asset: row, verified: false })
    return rows.slice(0, 12)
  }, [assets, providerMap])

  const activeLaunches = useMemo(() => {
    const rank = (row:any) => {
      const state = String(row?.phase || row?.status || '').toLowerCase()
      return state === 'live' ? 0 : state === 'scheduled' ? 1 : state === 'approved' ? 2 : 3
    }
    return launches
      .filter(row => ['live','scheduled','approved'].includes(String(row?.phase || row?.status || '').toLowerCase()))
      .sort((a,b) => rank(a) - rank(b))
      .slice(0, 6)
  }, [launches])

  const recent = assets.slice(0, 12)
  const totalListings = collections.reduce((n,c)=>n+Number(c.listed_count||0),0)
  const totalAssets = collections.reduce((n,c)=>n+Number(c.asset_count||0),0)
  const floor = collections.map(c=>Number(c.floor_lamports||0)).filter(Boolean).sort((a,b)=>a-b)[0]
  const topCollections = collections.slice(0,10)

  return <>
    <section className="market-hero">
      <div>
        <span className="eyebrow"><Sparkles size={14}/> OnlyRose · Solana Mainnet</span>
        <h1>Trade Rose NFTs.<br/><em>Full market hub.</em></h1>
        <p>Collections, indexed NFTs, marketplace listings, launchpad releases, activity and permanent NFT pages in one real marketplace surface.</p>
        <div className="hero-actions">
          <Link className="btn primary" to="/collections">Explore collections</Link>
          <Link className="btn secondary" to="/launchpad">Open launchpad</Link>
        </div>
      </div>
      <div className="hero-market-card">
        <span>ROSE MARKET INDEX</span>
        <b>{number(collections.length)} collections</b>
        <strong>{number(listed.length || totalListings)} listed NFTs</strong>
        <small>{number(assets.length || totalAssets)} loaded NFTs</small>
        <button className="btn secondary" onClick={load}><RefreshCw size={15}/> Refresh market</button>
      </div>
    </section>

    <nav className="market-hub-tabs" aria-label="Marketplace sections">
      <a href="#listed">Listed</a>
      <a href="#collections">Collections</a>
      <a href="#launchpad">Launchpad</a>
      <a href="#explore">Explore NFTs</a>
      <a href="#activity">Activity</a>
    </nav>

    <div className="metric-grid">
      <div><span>Collections</span><b>{number(collections.length)}</b><small>Rose market index</small></div>
      <div><span>Loaded NFTs</span><b>{number(assets.length)}</b><small>Cached indexed assets</small></div>
      <div><span>Indexed listings</span><b>{number(listed.length || totalListings)}</b><small>Provider rows override when verified</small></div>
      <div><span>Lowest floor</span><b>{sol(floor)}</b><small>Across indexed collections</small></div>
    </div>

    {error && <ErrorState error={error} retry={load}/>} 
    {loading && !assets.length && <Loading/>}

    <section className="market-section" id="listed">
      <div className="section-heading">
        <div>
          <span className="eyebrow"><ShoppingBag size={14}/> Trade</span>
          <h2>Live marketplace listings</h2>
          <p>Provider-verified listings are preferred. Rose index listings remain visible and open the permanent NFT page for live revalidation.</p>
        </div>
        <Link to="/listed">View all listed NFTs</Link>
      </div>
      {listed.length
        ? <div className="market-index-grid">{listed.map(({asset,verified}) => <MarketIndexCard key={asset.asset_id} asset={asset} verified={verified}/>)}</div>
        : <div className="quiet-panel">No indexed non-burnt listings are currently available.</div>}
    </section>

    <section className="market-section" id="collections">
      <div className="section-heading">
        <div>
          <span className="eyebrow"><Grid2X2 size={14}/> Collections</span>
          <h2>Marketplace collections</h2>
          <p>Real collection-address cards from the Rose market index.</p>
        </div>
        <div className="section-heading-actions">
          <CollectionViewToggle value={collectionView} onChange={setCollectionView}/>
          <Link to="/collections">View all collections</Link>
        </div>
      </div>
      {collectionView === 'cards' ? <CollectionCards rows={topCollections}/> : <CollectionTable rows={topCollections}/>} 
    </section>

    <section className="market-section" id="launchpad">
      <div className="section-heading">
        <div>
          <span className="eyebrow"><Rocket size={14}/> Launchpad</span>
          <h2>Collection launches</h2>
          <p>Live, scheduled and approved Rose collection launches.</p>
        </div>
        <Link to="/launchpad">View launchpad</Link>
      </div>
      {activeLaunches.length
        ? <div className="market-launch-grid">{activeLaunches.map((launch,i) => <MarketLaunchCard key={launch.id || launch.collection_address || i} launch={launch}/>)}</div>
        : <div className="quiet-panel">No active Rose launches are currently returned.</div>}
    </section>

    <section className="market-section" id="explore">
      <div className="section-heading"><div><h2>Explore NFTs</h2><p>Indexed non-burnt collection inventory from Rose's existing market database. Helius is no longer required just to render this section.</p></div></div>
      {recent.length
        ? <div className="market-index-grid">{recent.map(asset => <MarketIndexCard key={asset.asset_id} asset={asset} verified={providerMap.has(String(asset.asset_id))}/>)}</div>
        : <div className="quiet-panel">No indexed NFTs are currently available.</div>}
    </section>

    <section className="market-section" id="activity">
      <div className="section-heading">
        <div><span className="eyebrow"><ActivityIcon size={14}/> Network activity</span><h2>Recent marketplace activity</h2></div>
        <Link to="/activity">View activity</Link>
      </div>
      <div className="activity-list">
        {activity.slice(0,10).map((row,i)=><div className="activity-row" key={row.id || `${row.asset_id}-${i}`}>
          <b>{String(row.event_type||'EVENT').replaceAll('_',' ')}</b>
          <Link to={row.asset_id ? `/nft/${row.asset_id}` : '#'}>{row.asset_name || short(row.asset_id)}</Link>
          <span>{short(row.buyer_address || row.seller_address)}</span>
          <strong>{sol(row.price_lamports)}</strong>
          <small>{date(row.block_time || row.created_at)}</small>
        </div>)}
      </div>
    </section>
  </>
}
